import { IcbtCommunication } from "api/types";
import { Patient } from "core/types/index";
import { isThisMonth, isThisWeek } from "date-fns";
import { useMemo } from "react";
import { filter, map, partition, pipe, sortBy } from "remeda";

export const doneCommunicationCategories = [
  "week",
  "month",
  "all",
  "noFeedbackNeeded",
] as const;
export type Categories = (typeof doneCommunicationCategories)[number];

export type IcbtCommunicationWithPatient = IcbtCommunication & {
  patient?: Patient;
};
export type CategorizedIcbtCommunication = Map<
  Categories,
  IcbtCommunicationWithPatient[]
>;
/**
 * Takes list of IcbtCommunication and returns a Map of categorized IcbtCommunication that has feedback by feedback_date
 *
 * ***all:*** has all IcbtCommunication that has been sent ever
 *
 * ***week:*** has IcbtCommunication that has been sent this week
 *
 * ***month:*** has IcbtCommunication that has been sent this month
 *
 * **noFeedbackNeeded** has IcbtCommunication that has been marked as feedback date ignored
 *
 */
export const useCategorizedDoneCommunication = ({
  icbtCommunication,
  patientsToIncludeInCommunication: patients,
  ignoreFeedbackDateFeatureEnabled,
}: {
  icbtCommunication?: IcbtCommunication[];
  patientsToIncludeInCommunication?: Patient[];
  ignoreFeedbackDateFeatureEnabled: boolean;
}) =>
  useMemo(() => {
    if (!icbtCommunication)
      return new Map(
        Object.entries({
          week: [],
          month: [],
          all: [],
          noFeedbackNeeded: [],
        }),
      );
    return pipe(
      icbtCommunication,
      // Filter has a feedback_date and has feedback written or feedback date ignored
      filter(
        (request) =>
          Boolean(request.feedback_date) &&
          ((ignoreFeedbackDateFeatureEnabled &&
            Boolean(request.feedback_date_ignored_at)) ||
            request.feedbacks.some(
              (feedback) => feedback.feedback_status === "published",
            )),
      ),
      // Include patient data
      map((request) => {
        return {
          ...request,
          patient: patients?.find(
            (p) => p.userId === Number(request.patient_user_id),
          ),
        };
      }),
      // Partition to 'noFeedbackNeeded' based on feedback_date_ignored_at
      partition(
        (request) =>
          ignoreFeedbackDateFeatureEnabled &&
          Boolean(request.feedback_date_ignored_at),
      ),
      // Partition the rest into week and month
      ([noFeedbackNeeded, rest]) => [
        noFeedbackNeeded,
        ...partition(rest, (r) =>
          isThisWeek(new Date(r.feedback_date), { weekStartsOn: 1 }),
        ),
      ],
      // Partition the rest into this month and final rest
      ([noFeedbackNeeded, week, rest]) => [
        noFeedbackNeeded,
        week,
        ...partition(rest, (r) => isThisMonth(new Date(r.feedback_date))),
      ],
      // Include 'week' to 'month' and 'month' to 'all', but exclude 'noFeedbackNeeded'
      ([noFeedbackNeeded, week, month, all]) => [
        noFeedbackNeeded,
        week,
        [...week, ...month],
        [...week, ...month, ...all],
      ],
      // Sort each category by feedback_date
      map((category) => {
        return pipe(
          category,
          sortBy((c) => -new Date(c.feedback_date).getTime()),
        );
      }),
      // Return a map with categories
      ([noFeedbackNeeded, week, month, all]) =>
        new Map<Categories, IcbtCommunicationWithPatient[]>(
          Object.entries({
            noFeedbackNeeded,
            week,
            month,
            all,
          }) as [Categories, IcbtCommunicationWithPatient[]][],
        ),
    );
  }, [icbtCommunication, patients, ignoreFeedbackDateFeatureEnabled]);
